import React, { createContext, useContext, useEffect, useState } from "react";
import api, { setAccessToken, getAccessToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=user
  const [perms, setPerms] = useState(new Set()); // permisos efectivos (slugs)
  const [error, setError] = useState("");

  const loadPerms = async () => {
    try {
      const { data } = await api.get("/permissions/me");
      const efectivos = data?.efectivos || {};
      const set = new Set(Object.keys(efectivos).filter((k) => efectivos[k]));
      setPerms(set);
    } catch (e) {
      setPerms(new Set());
    }
  };

  useEffect(() => {
    (async () => {
      const hasToken = !!getAccessToken();
      if (!hasToken) { setUser(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        await loadPerms();
        setUser(data);
      } catch (e) {
        // Token inválido/expirado → limpiar y marcar como anónimo
        if (e?.response?.status !== 401) console.warn("Auth /me failed:", e?.message);
        setAccessToken(null);
        setUser(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAccessToken(data.access_token);
      await loadPerms();
      setUser(data.user);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Credenciales inválidas");
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Backend cookie clear puede fallar si la sesión ya expiró - es seguro continuar
      if (process.env.NODE_ENV === "development") console.debug("logout backend call failed:", e?.message);
    }
    setAccessToken(null);
    setUser(false);
    setPerms(new Set());
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      await loadPerms();
      setUser(data);
      return data;
    } catch (e) {
      return null;
    }
  };

  const hasPermission = (slug) => {
    if (!slug) return true;
    // admin tiene todo (defensa adicional en frontend)
    if ((user?.roles || []).includes("admin")) return true;
    return perms.has(slug);
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, refreshUser, error, hasPermission, hasRole: (r) => {
      const roles = user?.roles || [];
      if (roles.includes("admin")) return true;
      if (Array.isArray(r)) return r.some((x) => roles.includes(x));
      return roles.includes(r);
    } }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
